/**
 * @description
 * 特定のアプリケーションでの特別対応処理をまとめたもの
 */

import { Maybe, PostDetailQuery } from '@/clients/utoniq-core/schema'
import { config } from '@/config'

/**
 * @description
 * OAuthクライアント制限のため、RESERVED_CLIENT_IDが取得できないアプリはTwitter認証を非表示にする
 * https://app.clickup.com/t/864egmuz3
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideTwitterAuth = () => {
  const TARGET_APPLICATION_KEYS = [
    'diica',
    'ndp',
    'kanonshizaki',
    'kowacomi',
    'rocomacoaco',
    'liella',
    'liyuu',
    'kiramune',
    'starrise',
    'waipoke',
    'playdeck',
    'aqours',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ポスト詳細のコメント一覧から、特定のユーザーのコメントを除外する
 * @param {Maybe<PostDetailQuery['post']>} post ポスト詳細のデータ
 * @returns {Maybe<PostDetailQuery['post']>} コメントフィルタリング後のポスト詳細のデータ
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=2508-56289&t=4X9KSOYM0lNd7t3M-1
 */
export const individualFilterBannedUserPostComment = (
  post: Maybe<PostDetailQuery['post']>
) => {
  if (!post?.replies) return post

  const TARGET_USER_IDS = ['mMdsjMWaciQIxHgPvG0nZBDiwGl1']

  return {
    ...post,
    replies: {
      ...post?.replies,
      edges: post?.replies?.edges?.filter(
        ({ node }) => !TARGET_USER_IDS.includes(node?.responder?.id ?? '')
      ),
    },
  }
}

/**
 * アイテム詳細ページの補足リンク領域にて、一部アプリでアイテム「情報」ページ導線の非表示フラグをONにする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 * cf SupplementLinkArea
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=3885-91376&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHiddenItemInfo = () => {
  const TARGET_APPLICATION_KEYS = ['omenz']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ユーザーが保有しているアイテムの発行数を非表示にする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 *
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=2628-55046&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHideItemIssuedNumber = () => {
  const TARGET_APPLICATION_KEYS = ['omenz']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 一部アプリのガチャページにおいて、在庫数・アイテム発行数を非表示にする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-193363&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=180-22&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-193152&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-194000&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHideGachaRemainingItemCount = () => {
  const TARGET_APPLICATION_KEYS = [
    'liella',
    'kiramune',
    'liyuu',
    'omenz',
    'waipoke',
    'aqours',
    'machizero',
    'lana',
    'diica',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アイテム編集画面でSKU毎の残数を表示するかどうかのフラグ
 * パフォーマンスのため、アサイーのみ表示する
 *
 * @return {Boolean} true: 表示する, false: 表示しない
 */
export const individualRemainingItemCountBySKU = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 氏名をカタカナで入力させるかどうかの判定
 * アサイーなど、現場で用いるアプリケーションでは氏名をカタカナで入力させる
 *
 * @return {Boolean} true: カタカナで入力させる, false: カタカナで入力させない
 */
export const individualUseFullNameKatakana = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * チュートリアルのスキップ導線を非表示にするかどうかの判定
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideTutorialSkip = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ストア詳細ページのシェアボタン・情報の導線を非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideStoreSupplementLink = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ストア詳細ページのアイテム情報の欄(種別・発行数・所有枚数)の表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideStoreItemInfo = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アーティストトップのグラデーションマスクの表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideArtistTopGradientMask = () => {
  const TARGET_APPLICATION_KEYS = ['yokohamameguri', 'iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アーティストトップの新着ポスト表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideArtistNewPosts = () => {
  const TARGET_APPLICATION_KEYS = ['lana', 'standalone_stg']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ウォレットを非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideWallet = () => {
  const TARGET_APPLICATION_KEYS = ['playdeck']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 配送を非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideDelivery = () => {
  const TARGET_APPLICATION_KEYS = ['playdeck']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * グローバルホームでフィードを非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideFeed = () => {
  const TARGET_APPLICATION_KEYS = ['machizero']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * グローバルホームでアーティストを非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideArtists = () => {
  const TARGET_APPLICATION_KEYS = ['machizero']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 配送ステータス「未申請」を設けない
 * 配送管理画面の初期表示タブに影響する
 *
 * @return {Boolean} true: 「未申請」を設けない, false: 「未申請」を設ける
 */
export const individualOmitDeliveryStatusUnapplied = () => {
  const TARGET_APPLICATION_KEYS = ['machizero']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * レスポンシブ表示を行うかどうか
 *
 * 2025/08/04時点
 * 2025年6月に対応したレスポンシブ対応で再検討が必要な部分があり、一部のサイトを除いて展開できない。
 * ブランチの統合に伴い、展開する必要のあるサイトのみレスポンシブ表示を行うよう、個別分岐を設ける。
 * レスポンシブ対応の再検討が終わり、全サイトに展開できるようになったら除外する。
 *
 * @return {Boolean} true: レスポンシブ表示対象, false: レスポンシブ表示対象外
 */
export const individualDisplayResponsive = () => {
  const TARGET_APPLICATION_KEYS = [
    'jonishirei',
    'jonishirei-stg',
    'abarerukun',
    'abarerukun-stg',
    'yt-unaverage',
    'yt-unaverage-stg',
    'lana',
    'lana-stg',
    'standalone_stg',
    'kyra',
    'kyra-stg',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ご利用ガイドを非表示にする
 *
 * ご利用ガイドを個別に作成しているサイトは標準のご利用ガイドを非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideHelp = () => {
  const TARGET_APPLICATION_KEYS = [
    'yt-unaverage',
    'yt-unaverage-stg',
    'etoneigefc',
    'etoneigefc-stg',
    'kyra',
    'kyra-stg',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * お問い合わせフォームを非表示にする
 *
 * 2025年8月時点：お問い合わせの運用負荷を下げるため、問い合わせフローを全体的に見直し中。
 * ここで定義したアプリでは、個別にお問い合わせへの導線を設けるため、見直し完了までは個別に非表示の分岐を作る。
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideInquiry = () => {
  const TARGET_APPLICATION_KEYS = [
    'yt-unaverage',
    'yt-unaverage-stg',
    'etoneigefc',
    'etoneigefc-stg',
    'kyra',
    'kyra-stg',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * @description
 * 24時間限定公開のストーリー機能を表示するかどうか
 *
 * WAIWAI（Etoneige サイトの顧客）からの要望で開発された、
 * 画像または YouTube 以外の動画ポストを24時間限定で公開する機能の表示制御
 *
 * フィードが表示されるグローバルホームを持たず、
 * ポスト機能も使用しないことを前提とした
 * Etoneige でのみ使用されることを想定している。
 *
 * 2025年8〜9月の開発リソース逼迫により、
 * フィードに関連する functions の動作検証が完了していない状態だが、
 * デリバリーを優先してフィードへの動線がなく直接的な影響が発生しない
 * Etoneige でのみ展開することとなった。
 *
 * 本機能は ⚠️ Etoneige 以外には展開しない ⚠️ こと。
 * 現状のコードはフィード表示での正常な動作を担保できていないため、
 * フィード表示がある他サイトに導入した場合、
 * 予期せぬ不具合が発生するリスクがある。
 *
 * Etoneige 以外で本機能を展開する際には、必ず以下の対応を完了させること。
 * ・API による ON/OFF 制御の開発
 * ・フィードに関連する functions の挙動の調査と動作検証
 *
 * @return {Boolean} true: 表示する, false: 表示しない
 */
export const individualShowStory = () => {
  const TARGET_APPLICATION_KEYS = ['etoneigefc', 'etoneigefc-stg']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ネイティブアプリ → WebブラウザへのHTMLフォームのリレーページを表示するかどうか
 *
 * ポスト投稿でチケットぴあなどのルート限定タグを使用する、かつネイティブアプリがある場合は指定を入れる
 *
 * @returns {Boolean} true: 表示する, false: 表示しない
 */
export const individualShowHtmlFormRelay = () => {
  const TARGET_APPLICATION_KEYS = ['etoneigefc', 'etoneigefc-stg']
  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}
